// The machine-readable env contract: every wasm import the emitted game.wasm
// declares, and the builtin → callback-dispatcher map. This is the boundary
// between the (now Rust/WASM) compiler and the host runtime — `runtime/base-env.ts`
// builds every host env from IMPORTS, and `runtime/async-ops.ts` routes callback
// completions through CALLBACK_DISPATCHERS, so neither can drift from what the
// compiler emits.
//
// It is DERIVED from the checker's BUILTIN_FUNCTIONS registry (types/builtins.ts),
// the single source of truth for the builtin surface. Extracted from the old TS
// codegen so it survives that pipeline's removal (docs/rust-compiler-migration.md).
import { ValType } from "../wasm/module.js";
import { BUILTIN_FUNCTIONS } from "../types/builtins.js";
// Imports the codegen itself targets when lowering language constructs — not
// script-visible builtins, so they have no BUILTIN_FUNCTIONS entry. makeBaseEnv
// implements every one of these universally.
const RUNTIME_IMPORTS = [
    // print(...) — variadic in source, lowered to repeated calls, split by
    // signedness (print_i32 signed decimal, print_u32 unsigned).
    { name: "print_i32", params: [ValType.i32], results: [] },
    { name: "print_u32", params: [ValType.i32], results: [] },
    { name: "print_str", params: [ValType.i32], results: [] },
    { name: "print_end", params: [], results: [] },
    // runtime array allocation
    { name: "alloc_array", params: [ValType.i32, ValType.i32], results: [ValType.i32] },
    // copy_array(srcPtr, elementBytes) — fresh array, same length/size as src,
    // byte-copied.
    { name: "copy_array", params: [ValType.i32, ValType.i32], results: [ValType.i32] },
    // concat_array(aPtr, bPtr, elementBytes) — fresh array of a.length+b.length,
    // a's then b's data byte-copied.
    { name: "concat_array", params: [ValType.i32, ValType.i32, ValType.i32], results: [ValType.i32] },
    // alloc_struct(inlineSize) — bump-allocate a struct's inline footprint,
    // zero-filled; returns the struct pointer.
    { name: "alloc_struct", params: [ValType.i32], results: [ValType.i32] },
    // init_array_field(headerAddr, length, elementBytes) — write a fresh 16-byte
    // array header inside a struct's footprint and allocate its data block.
    { name: "init_array_field", params: [ValType.i32, ValType.i32, ValType.i32], results: [] },
    // String concat — host helper for `string + string`.
    { name: "concat_strings", params: [ValType.i32, ValType.i32], results: [ValType.i32] },
    // Number→string, split by signedness (int_to_string signed, uint_to_string
    // unsigned), for mixed `string + number`.
    { name: "int_to_string", params: [ValType.i32], results: [ValType.i32] },
    { name: "uint_to_string", params: [ValType.i32], results: [ValType.i32] },
    // String equality — host byte-wise content comparison, returning 1/0.
    { name: "equal_strings", params: [ValType.i32, ValType.i32], results: [ValType.i32] },
    // Truncate — fresh string capped at the first N bytes; N is an i32 const.
    { name: "truncate_string", params: [ValType.i32, ValType.i32], results: [ValType.i32] },
    // Reserve a bounded `string<N>` slot: copy into a fresh buffer of capacity N.
    { name: "string_reserve", params: [ValType.i32, ValType.i32], results: [ValType.i32] },
    // Inline `string<N>[M]` cell access (docs/memory-model.md §"Stage 2").
    { name: "string_cell_read", params: [ValType.i32], results: [ValType.i32] },
    { name: "string_cell_write", params: [ValType.i32, ValType.i32, ValType.i32], results: [] },
    // Escaped-generation write-barrier (docs/memory-model.md §"Stage 2").
    { name: "promote_escaped", params: [ValType.i32, ValType.i32, ValType.i32], results: [ValType.i32] },
    { name: "retain", params: [ValType.i32], results: [] },
    { name: "release", params: [ValType.i32, ValType.i32, ValType.i32], results: [] },
    // reassign_escaped_string(oldPtr, newPtr) — in-place-capable reassignment of
    // a persistent string slot (docs/memory-model.md §"Open follow-ons").
    { name: "reassign_escaped_string", params: [ValType.i32, ValType.i32], results: [ValType.i32] },
    // register_channel(channel) — records that this member listens on `channel`.
    { name: "register_channel", params: [ValType.i32], results: [] },
];
// Lower one checker-registry entry to its wasm import signature.
function lowerBuiltin(fn, importName) {
    return {
        name: importName,
        params: fn.params.map(() => ValType.i32),
        results: fn.returnType.kind === "void" ? [] : [ValType.i32],
    };
}
export const IMPORTS = [
    ...RUNTIME_IMPORTS,
    ...BUILTIN_FUNCTIONS
        .filter(fn => fn.importName !== null)
        .map(fn => lowerBuiltin(fn, fn.importName ?? fn.name)),
];
// Maps each builtin that owns a callback contract to the per-contract dispatcher
// the host invokes on completion. Derived from the `dispatcher` names on the
// contracts in types/builtins.ts.
export const CALLBACK_DISPATCHERS = Object.freeze(Object.fromEntries(BUILTIN_FUNCTIONS.flatMap(fn => {
    const cb = fn.params.find(p => p.callback !== undefined);
    return cb ? [[fn.name, cb.callback.dispatcher]] : [];
})));
//# sourceMappingURL=import-contract.js.map